<script setup>
import { defineEmits, defineProps, onMounted, ref } from "vue";
import apiClient from "@/axiosConfig";

defineProps({ canManage: Boolean });
const emit = defineEmits(["changed"]);
const state = ref(null);
const error = ref("");
const pending = ref(false);
const expanded = ref(false);
const account = ref(null);
const transaction = ref(null);
const editingAccount = ref(false);
const editingTransaction = ref(false);
const importData = ref(null);
const importPreview = ref(null);
const replaceImport = ref(false);
const confirmation = ref(null);
const challenge = ref(null);
const tan = ref("");
const decoupled = ref(false);

async function request(method, path, data) {
  pending.value = true;
  error.value = "";
  try {
    const response = await apiClient.request({ method, url: `/banking/mock/${path}`, data });
    return response.data;
  } catch (failure) {
    error.value = failure?.response?.data?.detail || "Testbank-Aktion fehlgeschlagen.";
    throw failure;
  } finally { pending.value = false; }
}
async function refresh() {
  try {
    const result = await request("get", "state");
    state.value = Array.isArray(result?.accounts) && Array.isArray(result?.transactions) ? result : null;
  }
  catch (failure) { if ([403, 404].includes(failure?.response?.status)) { state.value = null; error.value = ""; } }
}
function editAccount(value = {}) {
  editingAccount.value = Boolean(value.accountId);
  account.value = { accountId: value.accountId || "", iban: value.iban || "", bic: value.bic || "", currencyCode: value.currencyCode || "EUR", displayName: value.displayName || "", isActive: value.isActive ?? true };
  transaction.value = null;
}
function editTransaction(value = {}) {
  editingTransaction.value = Boolean(value.transactionId);
  const today = new Date().toISOString().slice(0, 10);
  transaction.value = { accountId: value.accountId || state.value.accounts[0]?.accountId || "", transactionId: value.transactionId || "", bookingDate: value.bookingDate || today, valueDate: value.valueDate || today, amount: value.amount ?? 0, currencyCode: value.currencyCode || "EUR", counterpartyName: value.counterpartyName || "", remittanceInformation: value.remittanceInformation || "" };
  account.value = null;
}
async function save(path, data) {
  try {
    await request("put", path, data);
    account.value = null; transaction.value = null;
    await refresh(); emit("changed");
  } catch { /* Error remains visible with the editable input. */ }
}
async function confirmOperation() {
  const action = confirmation.value;
  try {
    await request(action.method, action.path);
    confirmation.value = null;
    await refresh(); emit("changed");
  } catch { /* Do not discard the requested action on a conflict. */ }
}
async function exportData() {
  try {
    const data = await request("get", "export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const link = document.createElement("a"); link.href = url; link.download = "testbank.json";
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  } catch { /* The request error is visible in the panel. */ }
}
async function selectImport(event) {
  importData.value = null; importPreview.value = null; error.value = "";
  const file = event.target.files[0];
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) { error.value = "Importdatei darf höchstens 2 MB groß sein."; return; }
  try { importData.value = JSON.parse(await file.text()); }
  catch { error.value = "Die Datei enthält kein gültiges JSON."; }
}
async function importFile(preview) {
  try {
    const result = await request("post", "import", { accounts: importData.value.accounts, transactions: importData.value.transactions, replace: replaceImport.value, preview });
    if (preview) { importPreview.value = result; return; }
    importData.value = null; importPreview.value = null;
    await refresh(); emit("changed");
  } catch { importPreview.value = null; }
}
async function startTan() {
  try { challenge.value = await request("post", "tan-challenges", { decoupled: decoupled.value, operation: "transactions" }); tan.value = ""; }
  catch { /* The request error is visible in the panel. */ }
}
async function submitTan() {
  try { challenge.value = await request("post", `tan-challenges/${encodeURIComponent(challenge.value.id)}/submit`, { tan: tan.value, approved: challenge.value.decoupled }); }
  catch { /* The request error is visible in the panel. */ }
}
onMounted(refresh);
</script>

<template>
  <section v-if="state" class="card p-3 mb-3" aria-labelledby="mock-bank-heading">
    <div class="d-flex justify-content-between align-items-center">
      <h2 id="mock-bank-heading" class="h5 mb-0">Testbank</h2>
      <button class="btn btn-outline-secondary btn-sm" :aria-expanded="expanded" @click="expanded = !expanded">{{ expanded ? 'Schließen' : 'Testdaten verwalten' }}</button>
    </div>
    <template v-if="expanded">
      <p class="small text-muted mt-2">Lokale Testdaten für Banking und OPOS. Es werden keine Bankaufträge ausgelöst. Bereits gebuchte Umsätze bleiben durch das Journal geschützt.</p>
      <p v-if="error" class="alert alert-danger" role="alert">{{ error }}</p>
      <div class="d-flex flex-wrap gap-2 mb-3">
        <button v-if="canManage" class="btn btn-primary btn-sm" :disabled="pending" @click="editAccount()">Testkonto anlegen</button>
        <button v-if="canManage" class="btn btn-primary btn-sm" :disabled="pending || !state.accounts.length" @click="editTransaction()">Testumsatz anlegen</button>
        <button class="btn btn-outline-secondary btn-sm" :disabled="pending" @click="exportData">JSON exportieren</button>
        <button v-if="canManage" class="btn btn-outline-danger btn-sm" :disabled="pending" @click="confirmation = { method: 'post', path: 'reset', label: 'Testdaten auf die Ausgangswerte zurücksetzen' }">Testdaten zurücksetzen</button>
      </div>
      <div class="table-responsive"><table class="table table-sm"><thead><tr><th>Konto</th><th>IBAN</th><th>Aktiv</th><th></th></tr></thead><tbody>
        <tr v-for="entry in state.accounts" :key="entry.accountId"><td>{{ entry.displayName || entry.accountId }}</td><td>{{ entry.iban }}</td><td>{{ entry.isActive ? 'Ja' : 'Nein' }}</td><td v-if="canManage">
          <button class="btn btn-outline-secondary btn-sm me-1" :disabled="pending" @click="editAccount(entry)">Bearbeiten</button>
          <button class="btn btn-outline-danger btn-sm" :disabled="pending" @click="confirmation = { method: 'delete', path: `accounts/${encodeURIComponent(entry.accountId)}`, label: `Testkonto ${entry.accountId} löschen` }">Löschen</button>
        </td></tr>
      </tbody></table></div>
      <div class="table-responsive"><table class="table table-sm"><thead><tr><th>Konto / Umsatz</th><th>Gegenpartei</th><th>Betrag</th><th></th></tr></thead><tbody>
        <tr v-for="entry in state.transactions" :key="`${entry.accountId}/${entry.transactionId}`"><td>{{ entry.accountId }} / {{ entry.transactionId }}</td><td>{{ entry.counterpartyName }}</td><td>{{ entry.amount }} {{ entry.currencyCode }}</td><td v-if="canManage">
          <button class="btn btn-outline-secondary btn-sm me-1" :disabled="pending" @click="editTransaction(entry)">Bearbeiten</button>
          <button class="btn btn-outline-danger btn-sm" :disabled="pending" @click="confirmation = { method: 'delete', path: `accounts/${encodeURIComponent(entry.accountId)}/transactions/${encodeURIComponent(entry.transactionId)}`, label: `Testumsatz ${entry.transactionId} löschen` }">Löschen</button>
        </td></tr>
      </tbody></table></div>
      <form v-if="canManage && account" class="border rounded p-3 mb-3" @submit.prevent="save('accounts', account)">
        <h3 class="h6">Testkonto bearbeiten</h3>
        <div class="row g-2">
          <label class="col-md-4">Konto-ID<input v-model="account.accountId" class="form-control" :readonly="editingAccount" required maxlength="100" /></label>
          <label class="col-md-4">Anzeigename<input v-model="account.displayName" class="form-control" maxlength="200" /></label>
          <label class="col-md-4">Währung<input v-model="account.currencyCode" class="form-control" required pattern="[A-Z]{3}" /></label>
          <label class="col-md-6">IBAN<input v-model="account.iban" class="form-control" /></label>
          <label class="col-md-6">BIC<input v-model="account.bic" class="form-control" /></label>
        </div>
        <label class="form-check my-2"><input v-model="account.isActive" class="form-check-input" type="checkbox" /> Aktiv</label>
        <button class="btn btn-primary btn-sm me-2" :disabled="pending">Testkonto speichern</button><button type="button" class="btn btn-outline-secondary btn-sm" @click="account = null">Abbrechen</button>
      </form>
      <form v-if="canManage && transaction" class="border rounded p-3 mb-3" @submit.prevent="save('transactions', transaction)">
        <h3 class="h6">Testumsatz bearbeiten</h3><div class="row g-2">
          <label class="col-md-4">Konto<select v-model="transaction.accountId" class="form-select" :disabled="editingTransaction" required><option v-for="entry in state.accounts" :key="entry.accountId" :value="entry.accountId">{{ entry.displayName || entry.accountId }}</option></select></label>
          <label class="col-md-4">Umsatz-ID<input v-model="transaction.transactionId" class="form-control" :readonly="editingTransaction" required maxlength="200" /></label>
          <label class="col-md-4">Währung<input v-model="transaction.currencyCode" class="form-control" required pattern="[A-Z]{3}" /></label>
          <label class="col-md-4">Buchungsdatum<input v-model="transaction.bookingDate" class="form-control" type="date" required /></label>
          <label class="col-md-4">Valutadatum<input v-model="transaction.valueDate" class="form-control" type="date" required /></label>
          <label class="col-md-4">Betrag<input v-model.number="transaction.amount" class="form-control" type="number" step="0.01" required /></label>
          <label class="col-md-6">Gegenpartei<input v-model="transaction.counterpartyName" class="form-control" maxlength="200" /></label>
          <label class="col-md-6">Verwendungszweck<input v-model="transaction.remittanceInformation" class="form-control" maxlength="1000" /></label>
        </div><button class="btn btn-primary btn-sm mt-2 me-2" :disabled="pending">Testumsatz speichern</button><button type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="transaction = null">Abbrechen</button>
      </form>
      <div v-if="canManage" class="border rounded p-3 mb-3">
        <label for="mock-bank-import">Testbank-JSON importieren</label><input id="mock-bank-import" type="file" accept=".json,application/json" class="form-control" @change="selectImport" />
        <label class="form-check my-2"><input v-model="replaceImport" type="checkbox" class="form-check-input" @change="importPreview = null" /> Vorhandene Testdaten ersetzen</label>
        <button class="btn btn-outline-primary btn-sm me-2" :disabled="pending || !importData" @click="importFile(true)">Import prüfen</button>
        <template v-if="importPreview"><span>Geprüft: {{ importData.accounts?.length || 0 }} Konten, {{ importData.transactions?.length || 0 }} Umsätze. </span><button class="btn btn-primary btn-sm" :disabled="pending" @click="importFile(false)">Import bestätigen</button></template>
      </div>
      <div v-if="canManage" class="border rounded p-3">
        <h3 class="h6">TAN-Ablauf simulieren</h3><p class="small">Nur für die Testbank: Test-TAN 123456. Keine echte Bank-TAN eingeben.</p>
        <label class="form-check mb-2"><input v-model="decoupled" type="checkbox" class="form-check-input" /> Freigabe in separater App simulieren</label>
        <button class="btn btn-outline-primary btn-sm" :disabled="pending" @click="startTan">Testfreigabe starten</button>
        <template v-if="challenge"><p class="mt-2">{{ challenge.challengeText }} · Status: {{ challenge.status }}</p>
          <form v-if="challenge.status === 'TanRequired'" @submit.prevent="submitTan">
            <label v-if="!challenge.decoupled">Test-TAN<input v-model="tan" class="form-control" pattern="[0-9]{6}" required /></label>
            <button class="btn btn-primary btn-sm mt-2" :disabled="pending">{{ challenge.decoupled ? 'Testfreigabe bestätigen' : 'Test-TAN senden' }}</button>
          </form>
        </template>
      </div>
      <div v-if="confirmation" role="dialog" aria-modal="false" aria-label="Testdaten ändern" class="alert alert-warning mt-3">
        <p>{{ confirmation.label }}?</p><button class="btn btn-danger btn-sm me-2" :disabled="pending" @click="confirmOperation">Bestätigen</button><button class="btn btn-outline-secondary btn-sm" :disabled="pending" @click="confirmation = null">Abbrechen</button>
      </div>
    </template>
  </section>
</template>
